import { jsx as _jsx } from "react/jsx-runtime";
import { createOrderRedirectPayment, isEmptyArray, orderHasInProgressAction, registerOrderAction } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useUnmountEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { useCallback, useContext, useRef, useState } from 'react';
import { OrderPaymentFormContextInstance } from '../types/context';
import { RedirectPaymentMethodStatus } from '../types/redirect-payment-method-status';
import { getUrlExcludingRedirectToken } from '../utils/redirect-token-url-parameter';
import { OrderPaymentFormMessageType } from './message';
import { OrderPaymentFormRedirectPaymentMethod } from './redirect-payment-method';
const b = bemClassBuilder('v-order-payment-form-redirect-payment-methods');
export const OrderPaymentFormRedirectPaymentMethods = observer(props => {
    const { config, events } = useContext(OrderPaymentFormContextInstance);
    const { redirectReturnUrl } = config;
    const { paymentMethods, setError } = props;
    const [selectedRedirectPaymentMethod, setSelectedRedirectPaymentMethod] = useState();
    const deregisterRedirectingAction = useRef();
    useUnmountEffect(() => {
        var _a;
        // enable order components
        (_a = deregisterRedirectingAction.current) === null || _a === void 0 ? void 0 : _a.call(deregisterRedirectingAction);
        deregisterRedirectingAction.current = undefined;
    });
    const navigateToRedirectUrl = useCallback((redirectUrl) => {
        // There can be a delay on slower devices while redirecting, so the payment method status must stay disabled until redirected.
        // Limitation: we can't track if the user cancels the redirect, so if that happens the payment methods will stay disabled and they must refresh the page.
        deregisterRedirectingAction.current = registerOrderAction('navigate-to-redirect-url');
        // Don't use window.location.replace as this prevents 'back' navigation
        window.location.href = redirectUrl;
    }, []);
    const handleRedirectPaymentMethodButtonClick = useCallback(async (redirectPaymentMethod) => {
        var _a;
        setSelectedRedirectPaymentMethod(redirectPaymentMethod);
        (_a = events.onPaymentStarted) === null || _a === void 0 ? void 0 : _a.call(events);
        try {
            const result = await createOrderRedirectPayment({
                webPaymentMethodId: redirectPaymentMethod.id,
                redirectReturnUrl: redirectReturnUrl !== null && redirectReturnUrl !== void 0 ? redirectReturnUrl : getUrlExcludingRedirectToken()
            });
            navigateToRedirectUrl(result.redirectUrl);
        }
        catch (e) {
            setSelectedRedirectPaymentMethod(undefined);
            setError(OrderPaymentFormMessageType.FailedToInitialiseConnector);
        }
    }, [events, navigateToRedirectUrl, redirectReturnUrl, setError]);
    if (isEmptyArray(paymentMethods))
        return null;
    return (_jsx("div", Object.assign({ className: b.block() }, { children: paymentMethods.map(method => (_jsx(OrderPaymentFormRedirectPaymentMethod, { paymentMethod: method, status: getRedirectPaymentMethodStatus(method, selectedRedirectPaymentMethod), onClick: handleRedirectPaymentMethodButtonClick }, method.id))) })));
});
OrderPaymentFormRedirectPaymentMethods.displayName = 'OrderPaymentFormRedirectPaymentMethods';
const getRedirectPaymentMethodStatus = (method, selectedMethod) => {
    if (selectedMethod && method.id === selectedMethod.id)
        return RedirectPaymentMethodStatus.Loading;
    const hasInProgressAction = orderHasInProgressAction();
    return hasInProgressAction || selectedMethod
        ? RedirectPaymentMethodStatus.Disabled
        : RedirectPaymentMethodStatus.Active;
};
